@let footerBGColorClass =
  styleService.getColorClass(config.config()?.footer?.backgroundColor ?? DEFAULT_CONFIG.footer?.backgroundColor, 'bg');
<footer
  role="contentinfo"
  [aria-label]="'footer.name' | translate"
  class="w-full"
  [ngClass]="[footerBGColorClass]"
>
  @let initData = informationService.init();
  <!-- Infoarea & Menuarea -->
  @let infoarea = configInfoarea();
  @let defaultInfoarea = DEFAULT_CONFIG.footer?.infoarea;
  <!-- Padding — resolved by CSS so server and client render the same markup -->
  @let footerPadding = config.config()?.footer?.padding ?? DEFAULT_CONFIG.footer?.padding;

  <div
    class="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12"
    [ngClass]="[
      styleService.getColorClass(infoarea?.text?.color ?? defaultInfoarea?.text?.color, 'text'),
      styleService.getFontClasses(infoarea?.text?.font ?? defaultInfoarea?.text?.font),
      useContainer() ? 'container mx-auto' : ''
    ]"
    [gsResponsive]="{ padding: footerPadding }"
  >
    <!-- Logo — Mobile: Top (order-1), Desktop: Left top -->
    <div class="order-1 flex flex-col lg:col-start-1 lg:row-start-1">
      @if (initData && initData.logo && infoarea?.logo?.active) {
        @let footerLogo = initData.logoAlternative ?? initData.logo;
        <a [href]="logoLink()" [attr.aria-label]="'header.aria.homepage' | translate" class="relative mt-2 mb-8 block h-16 w-32 lg:mt-7 lg:h-24 lg:w-48 xl:w-56">
          @for (p of [footerLogo]; track p?.medium ?? 'nopic') {
            <img
              class="object-contain object-left"
              [ngSrc]="imageService.getEndpoint(p, 'medium')!"
              [ngSrcset]="imageService.DEFAULT_SRCSET"
              [loaderParams]="imageService.getLoaderParams(p)"
              sizes="(max-width: 1024px) 128px, 224px"
              fill
              gsImageAccessibility
              [gsImgAlt]="'footer.infoarea.logo.alt' | translate"
              [gsImgSeo]="'footer.infoarea.logo.seo' | translate"
            />
          }
        </a>
      }
    </div>

    <!-- Menucolumns — Mobile: Centered (order-2), Desktop: Right column spanning both rows -->
    @let menuarea = configMenuarea();
    @let defaultMenuarea = DEFAULT_CONFIG.footer?.menuarea;
    <div
      class="order-2 mb-4 flex flex-col gap-5 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:grid lg:auto-rows-min"
      [style.grid-template-columns]="menuareaGridColumns()"
      [ngClass]="[
        styleService.getColorClass(menuarea?.items?.color ?? defaultMenuarea?.items?.color, 'text'),
        styleService.getFontClasses(menuarea?.items?.font ?? defaultMenuarea?.items?.font)
      ]"
      [gsResponsive]="{ 'font-size': menuarea?.items?.textSize ?? defaultMenuarea?.items?.textSize }"
    >
      <!-- Spacer: Hidden element to maintain spacing on desktop at one level underneath the branding height -->
      <div class="hidden w-full lg:col-span-full lg:block lg:h-24" aria-hidden="true"></div>
      <!-- iD is no primary-key here... -->
      @for (menu of footerMenu(); track $index) {
        <div class="flex flex-col">
          @let headingColorClass = styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text');
          @let menuUrl = menu.url?.trim() ?? '';
          <!-- Branch on the raw url (not the resolved link) so server and client render the same structure (SSR hydration) -->
          @if (menuUrl) {
            @let menuIsExternal = menuUrl?.toLowerCase()?.startsWith('http');
            <a
              class="p hover:underline"
              [attr.href]="system.getLink(menuUrl) || null"
              [target]="menuIsExternal ? '_blank' : '_self'"
              [attr.rel]="menuIsExternal ? 'noopener noreferrer' : null"
              [ngClass]="[headingColorClass]"
            >
              {{ menu.name }}
            </a>
          } @else {
            <span class="p" [ngClass]="[headingColorClass]">
              {{ menu.name }}
            </span>
          }
        </div>
      }
    </div>

    <!-- Icons — Mobile: Bottom (order-3), Desktop: Left top -->
    <div class="order-3 flex flex-col gap-4 lg:col-start-1 lg:row-start-2 lg:gap-12">
      <!-- Social Media Icons — Mobile: bottom (order-2), Desktop: top (order-1) -->
      @if ((infoarea?.socialMediaLinks?.active ?? defaultInfoarea?.socialMediaLinks?.active) && footerService.hasSocialMediaLinks()) {
        <span class="order-2 flex flex-1 flex-wrap gap-5 lg:order-1 lg:gap-11">
          @for (socialMedia of socialMediaLinks() | keyvalue; track socialMedia.key) {
            @if (socialMedia.value) {
              <a [href]="socialMedia.value" target="_blank" rel="noopener noreferrer" class="size-8 lg:size-9">
                @let socialMediaKey = socialMedia.key;
                @let socialIconName = SOCIAL_MEDIA_ICON_MAP[socialMediaKey];
                <gs-icon
                  [iconName]="socialIconName ?? socialMediaKey.toLowerCase()"
                  iconWeight="regular"
                  [ngClass]="[
                    styleService.getColorClass(infoarea?.socialMediaLinks?.color ?? defaultInfoarea?.socialMediaLinks?.color, 'text')
                  ]"
                  class="text-3xl lg:text-4xl"
                />
              </a>
            }
          }
        </span>
      }

      <!-- App Icons — Mobile: Top (order-1), Desktop: Bottom (order-2) -->
      @if ((infoarea?.appLinks?.active ?? defaultInfoarea?.appLinks?.active) && footerService.hasAppLinks()) {
        <span class="order-1 flex flex-row flex-wrap justify-center gap-x-12 gap-y-5 lg:order-2 lg:justify-start lg:gap-x-5 lg:gap-y-4">
          @for (app of appLinks() | keyvalue; track app.key) {
            @if (app.value?.trim()) {
              <a [href]="app.value" target="_blank" rel="noopener noreferrer">
                <img
                  class="h-8 w-auto object-contain lg:h-10"
                  style="aspect-ratio: 173 / 50"
                  [src]="`assets/images/appbutton/${app.key}.svg`"
                  [alt]="app.key"
                  gsImageAccessibility
                  [gsImgAlt]="app.key"
                  [gsImgSeo]="app.key"
                />
              </a>
            }
          }
        </span>
      }
    </div>

    <!-- Gesetzlicher Gewährleistungshinweis (EmpCo) — bewusst fest eingebaut und nicht konfigurierbar, damit er nie abgeschaltet werden kann -->
    <section class="order-4 flex flex-col gap-2 lg:col-span-2 lg:row-start-3" [attr.aria-label]="'footer.warranty.title' | translate">
      @let warrantyHeadingColorClass = styleService.getColorClass(menuarea?.heading?.color ?? defaultMenuarea?.heading?.color, 'text');
      <span class="p font-bold" [ngClass]="[warrantyHeadingColorClass]">{{ 'footer.warranty.title' | translate }}</span>
      <!-- Text in der Farbe der sichtbaren Menüeinträge (im Slim-Footer die Heading-Farbe) und in Schrift/Größe der Menüpunkte -->
      <p
        class="p"
        [ngClass]="[warrantyHeadingColorClass, styleService.getFontClasses(menuarea?.items?.font ?? defaultMenuarea?.items?.font)]"
        [gsResponsive]="{ 'font-size': menuarea?.items?.textSize ?? defaultMenuarea?.items?.textSize }"
      >
        {{ 'footer.warranty.text' | translate }}
        @if (footerService.warrantyLink(); as warrantyLink) {
          <a [href]="warrantyLink" class="underline hover:no-underline focus:no-underline">
            {{ 'footer.warranty.link' | translate }}
            <span class="sr-only">{{ 'footer.warranty.linkHint' | translate }}</span>
          </a>
        }
      </p>
    </section>
  </div>

  <!-- Bottombar -->
  @let bottombar = configBottombar();
  @let defaultBottombar = DEFAULT_CONFIG.footer?.bottombar;
  @if (bottombar?.active ?? defaultBottombar?.active) {
    <div
      class="p flex w-full flex-col items-center justify-between gap-3 lg:flex-row-reverse lg:py-3"
      [ngClass]="[
        styleService.getColorClass(bottombar?.backgroundColor ?? defaultBottombar?.backgroundColor, 'bg'),
        styleService.getColorClass(bottombar?.text?.color ?? defaultBottombar?.text?.color, 'text')
      ]"
      [gsResponsive]="{ 'font-size': bottombar?.text?.textSize ?? defaultBottombar?.text?.textSize }"
    >
      <div
        class="flex w-full flex-col items-center justify-between gap-3 lg:flex-row-reverse"
        [gsResponsive]="{ 'padding-inline': footerPadding }"
        [ngClass]="{ 'container mx-auto': useContainer() }"
      >
        <!-- Links -->
        <span
          class="flex w-full flex-wrap items-center justify-center gap-2 gap-y-3 md:gap-6 md:gap-y-3 lg:w-auto lg:justify-start xl:gap-10"
        >
          @if (footerService.cookieBannerActive) {
            <button
              data-cc="show-preferencesModal"
              (click)="footerService.showCookiePreferences()"
              [attr.aria-label]="'footer.bottombar.cookieSettings' | translate"
              class="hover:underline"
            >
              {{ 'footer.bottombar.cookieSettings' | translate }}
            </button>
          }
          @if (footerService.showRevocation()) {
            <span class="order-first w-full lg:order-0 lg:w-auto">
              <a
                [href]="footerService.revocationLink()"
                [ngClass]="[footerService.revocationButtonClasses]"
                [ngStyle]="footerService.revocationButtonStyles"
                class="inline-block w-fit text-center duration-200"
              >
                {{ 'footer.revocation.button' | translate }}
              </a>
            </span>
          }
          @for (item of initData?.menus?.['cs4-footerCopyright']; track item.name) {
            @let itemUrl = item.url?.trim() ?? '';
            <!-- Branch on the raw url (not the resolved link) so server and client render the same structure (SSR hydration) -->
            @if (itemUrl) {
              @let isExternal = itemUrl.toLowerCase().startsWith('http');
              <a
                [attr.href]="system.getLink(itemUrl) || null"
                [target]="isExternal ? '_blank' : '_self'"
                [attr.rel]="isExternal ? 'noopener noreferrer' : null"
                class="hover:underline"
              >
                {{ item.name }}
              </a>
            } @else {
              <span>{{ item.name }}</span>
            }
          }
        </span>
        <!-- Copyright -->
        @let companyName = companyInfo()?.contact?.company;
        @if (companyName) {
          <div class="text-center lg:text-start">© {{ footerService.currentYear }} {{ companyName }}</div>
        }
      </div>
    </div>
  }
</footer>
